import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

export interface PipelineData {
    type: string; // 'text', 'file', 'image', 'pdf', etc.
    content: any;
    fileName?: string;
    sourceTool?: string;
}

@Injectable({
    providedIn: 'root'
})
export class PipelineService {
    private readonly platformId = inject(PLATFORM_ID);
    private dataSubject = new BehaviorSubject<PipelineData | null>(null);
    data$ = this.dataSubject.asObservable();

    /**
     * Store data to be passed to another tool.
     * We persist text data to SessionStorage for survival across reloads.
     * Blobs/Files are kept in-memory only for security and performance.
     */
    transferData(data: PipelineData) {
        this.dataSubject.next(data);

        // Persist only stringifiable data
        if (typeof data.content === 'string' && isPlatformBrowser(this.platformId)) {
            try {
                sessionStorage.setItem('devspace_pipeline_data', JSON.stringify(data));
            } catch (e) {
                console.error('Failed to persist pipeline data', e);
            }
        }
    }

    /**
     * Retrieve and clear the transferred data.
     */
    consumeData(): PipelineData | null {
        let data = this.dataSubject.value;

        // Try to recover from session storage if in-memory is empty
        if (!data && isPlatformBrowser(this.platformId)) {
            try {
                const saved = sessionStorage.getItem('devspace_pipeline_data');
                if (saved) {
                    data = JSON.parse(saved);
                }
            } catch (e) {
                console.error('Failed to recover pipeline data', e);
            }
        }

        this.clearData();
        return data;
    }

    clearData() {
        this.dataSubject.next(null);
        if (isPlatformBrowser(this.platformId)) {
            try {
                sessionStorage.removeItem('devspace_pipeline_data');
            } catch (e) { /* ignore */ }
        }
    }
}
